import { useCallback, useEffect } from "react";
import { Link } from "react-router";
import { css } from "emotion";
import { isEmpty } from "lodash-es";
import { normalizeCSSClass, DesignTheme, Image, Z_INDEX } from "@certa/blocks";
import { Stack, Text, StudioLogo } from "@certa/blocks/thanos";

import { Alert } from "@certa/catalyst/components/Alert";
import {
  getDashboardsGenericRoute,
  getTasksRoute,
  getHomeRoute,
  getStudioGenericRoute,
  getDashboardIdFromPathname,
  getDashboardsRoute
} from "../../utils/routes";
import { NavLink } from "./NavLink";
import {
  defaultNavLinks,
  studioNavLinks,
  studioNavLinksWithDataObjectRoute,
  appBuilderNavLink
} from "./header.constants";
import { HeaderMode, STUDIO_HEADER_HEIGHT } from "../../constants";

import { usePlatformSettingsProcessID } from "@certa/queries/hooks/appBuilder.hooks";
import { PermissionTypes } from "@certa/types";
import { useCanViewTasks } from "../../hooks/useCanViewTasks";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { CreateCertaSupportDropdown } from "./HeaderHelpDropdown";

import { UserProfile } from "./components";
import { useLanguageStore } from "@certa/common/store/language/useLanguageStore";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import { useDashboardNewUX } from "@certa/common/toggles";
import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { useCanSeeCreateSupportButton } from "@certa/common/hooks/useCanSeeCreateSupportButton";
import { useShowHeaderDropdownOptions } from "@certa/common/hooks/useShowHeaderDropdownOptions";
import { SelectLanguage } from "@certa/common/components/SelectLanguage";
import { useHomePage } from "@certa/common/hooks/useHomePage";
import { useHeaderBanner } from "@certa/common/hooks/useHeaderBanner";

import {
  useCheckPermission,
  useCheckPermissionFn
} from "@certa/common/utils/permissions/Chowkidar";
import { useCanViewStudioObjects } from "../../hooks/useCanViewStudio2";
import type { LinkProps } from "./types";
import { useIsAPPBuilderAgentEnabled } from "../../toggles/useDesignAIToggles";
import { useAuth } from "@certa/auth/AuthenticationProvider";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";

// TODO: [VET-23313] Add header banner
type HeaderProps = {
  mode: HeaderMode;
};
const HEADERMODES_WITH_DISABLED_LANGUAGE_SELECTOR = [HeaderMode.STUDIO];

const Header = ({ mode = HeaderMode.DEFAULT }: HeaderProps) => {
  const impersonateeId = sessionStorage.getItem("impersonateeId");

  const { data: config } = useInstanceConfigQuery();

  const shouldShowLanguage: boolean =
    !isEmpty(config?.supported_languages) &&
    !HEADERMODES_WITH_DISABLED_LANGUAGE_SELECTOR.includes(mode);

  const authentication = useAuth();

  // This is to prevent banner flicker
  // when logged in user becomes the impersonatee for a split second
  // while the impersonatee details are being fetched from the server
  const isSameUserImpersonation =
    (impersonateeId && parseInt(impersonateeId)) === authentication?.user?.id;

  const isSupplierUser = authentication?.user?.kind === 2;

  const userName =
    authentication?.user?.first_name && authentication?.user?.last_name
      ? `${authentication.user.first_name} ${authentication.user.last_name}`
      : (authentication.user?.email ?? "");

  const isStudio = mode === HeaderMode.STUDIO;

  const canViewDashboard = useCheckPermission(
    PermissionTypes.CAN_VIEW_DASHBOARD
  );
  const canViewTasks = useCanViewTasks();
  const checkPermissions = useCheckPermissionFn();
  const isHomePageEnabled = useHomePage();

  const canViewStudioObjectsPermissions = useCanViewStudioObjects();
  const isAPPBuilderAgentEnabled = useIsAPPBuilderAgentEnabled();

  const canViewStudio2Permissions = useCheckPermission(
    PermissionTypes.CAN_ACCESS_STUDIO
  );
  const isStudioEnabled = config?.configuration.enable_studio;

  const canViewStudio2 = canViewStudio2Permissions && isStudioEnabled;
  const canViewStudioObjects =
    canViewStudioObjectsPermissions && isStudioEnabled;

  /**
   * navLinks hold the final links that needs to be displayed on the header
   * which is based upon which mode the header is presented in
   */
  let navLinks: LinkProps[];

  if (mode === HeaderMode.STUDIO) {
    if (canViewStudioObjects) {
      navLinks = studioNavLinksWithDataObjectRoute;
    } else {
      navLinks = studioNavLinks;
    }
    if (
      isAPPBuilderAgentEnabled &&
      !navLinks.some(linkDetails => linkDetails.id === "ai-app-builder")
    ) {
      navLinks.push(appBuilderNavLink);
    }
  } else {
    navLinks = defaultNavLinks;
  }
  const isDashboardNewUXEnabled = useDashboardNewUX();
  const dashboardId = getDashboardIdFromPathname();

  useEffect(() => {
    // TODO: remove isDashboardNewUXEnabled condition when new Dashboard UX is enabled on Prod
    if (isDashboardNewUXEnabled) {
      navLinks.map(navLink => {
        if (navLink.label === "Dashboards") {
          if (dashboardId) {
            navLink.route = getDashboardsRoute(dashboardId);
          } else {
            navLink.route = getDashboardsGenericRoute();
          }
        }
        return navLink;
      });
    }
  }, [isDashboardNewUXEnabled, dashboardId, mode, navLinks]);

  // TODO: Remove once Platform Settings UI (VET-34577) is done
  // Prefetches platform settings process ID
  usePlatformSettingsProcessID();

  const bannerConfig = useHeaderBanner();
  const showNavLink = (route: string) => {
    if (route === getHomeRoute()) return isHomePageEnabled;
    else if (route === getStudioGenericRoute()) return canViewStudio2;
    else return true;
  };

  const showHeaderDropdownOptions = useShowHeaderDropdownOptions();
  const { supportTicketData } = useCanSeeCreateSupportButton();

  const { isMobileResolution } = useScreenResolution();

  const updateUserLanguage = useLanguageStore(
    state => state.updateUserLanguage
  );

  const handleLanguageChange = useCallback(
    (value: string) => {
      updateUserLanguage(value);
    },
    [updateUserLanguage]
  );
  return (
    <DesignTheme
      className={normalizeCSSClass}
      style={{
        width: "100%",
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX["zindex-fixed"]
      }}
    >
      {impersonateeId && !isSameUserImpersonation && <ImpersonationBanner />}

      {bannerConfig && !isMobileResolution && (
        <Alert
          type={bannerConfig.type}
          message={bannerConfig.message}
          hasIcon={bannerConfig.hasIcon}
          width="100%"
        />
      )}
      {mode === HeaderMode.STUDIO && (
        <div
          className={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            column-gap: var(--s7);
            padding: 0 var(--s6);
            background-color: var(--neutral-0);
            width: 100%;
            height: ${STUDIO_HEADER_HEIGHT}px;
            max-height: ${STUDIO_HEADER_HEIGHT}px;
          `}
        >
          <Stack
            gap="s6"
            align="center"
            className={css`
              height: 100%;
            `}
          >
            {!isStudio && (
              <Link
                to={
                  isHomePageEnabled
                    ? getHomeRoute()
                    : canViewDashboard
                      ? getDashboardsGenericRoute()
                      : getTasksRoute()
                }
                tabIndex={0}
                className={css`
                  border-radius: 2px;
                  &:focus {
                    outline: 2px solid var(--colors-brand-400);
                    outline-offset: 2px;
                  }
                `}
              >
                {config?.logo ? (
                  <Image alt="Certa logo" src={config.logo} height="32px" />
                ) : (
                  <Text variant="h1-bold">{config?.name}</Text>
                )}
              </Link>
            )}
            {mode === HeaderMode.STUDIO ? (
              <Stack gap="s4">
                <StudioLogo width={80} />
              </Stack>
            ) : null}
            {!isMobileResolution &&
              navLinks
                .filter(link =>
                  isSupplierUser ? !link.hideForSuppliers : true
                )
                .filter(link => showNavLink(link.route))
                .filter(link =>
                  link.checkPermissions &&
                  !(link.label === "Tasks" && !canViewTasks)
                    ? checkPermissions(link.checkPermissions)
                    : true
                )
                .map((link, index) => <NavLink link={link} key={`${index}`} />)}
          </Stack>
          <Stack
            gap="s7"
            align="center"
            className={css`
              justify-content: flex-end;
              width: 100%;
            `}
          >
            {!isMobileResolution &&
            (showHeaderDropdownOptions.length || supportTicketData) ? (
              <CreateCertaSupportDropdown
                supportTicketData={supportTicketData}
                dropdownOptions={showHeaderDropdownOptions}
              />
            ) : null}

            {shouldShowLanguage && (
              <Stack
                style={{
                  maxWidth: 150
                }}
              >
                <SelectLanguage
                  onLanguageClick={(open: boolean) =>
                    open &&
                    analyticsService.track(
                      AnalyticsEvents.headerEvents
                        .HEADER_CLICK_LANGUAGE_DROPDOWN
                    )
                  }
                  onLanguageChange={handleLanguageChange}
                />
              </Stack>
            )}

            <UserProfile userName={userName} />
          </Stack>
        </div>
      )}
    </DesignTheme>
  );
};

export { Header };
